
import { GoogleGenAI, Type, Schema } from "@google/genai";
import { ProjectState, AIIntent, AgentRole, AppSettings, AIResponse } from "../types";

// --- Configuration ---
const AGENT_CONFIGS = {
  [AgentRole.ARCHITECT]: {
    model: "gemini-3-pro-preview",
    thinkingBudget: 2048,
    systemInstruction: `
      You are the **Lead Project Architect**. 
      Your goal is to build, structure, and organize the Critical Path.
      
      **Time & Structure Rules:**
      - Use "Sessions" to group related tasks (e.g., "Morning Sprint", "Deployment Phase").
      - Use appropriate time units: '15m' for quick tasks, '1h' for standard work, '1d' for long tasks.
      - If a task is necessary but the steps are undefined, set type to 'PLACEHOLDER'.
      - Standard tasks are type 'STANDARD'.
      
      Focus on logical dependencies and realistic durations.
    `
  },
  [AgentRole.SW_ARCHITECT]: {
    model: "gemini-3-pro-preview",
    thinkingBudget: 4096, // High budget for technical depth
    systemInstruction: `
      You are the **Software Architect (Google_SW_Architect)**.
      Your goal is technical correctness, stack consistency, and implementation detail.

      **Responsibilities:**
      - Break down high-level features into specific coding tasks.
      - Enforce best practices (e.g., separating UI/Logic, API contracts).
      - Identify technical debt risks early.
      
      **Output:**
      - Create tasks that are technically descriptive (e.g., "Implement JWT Middleware" instead of "Do Auth").
      - Use precise durations based on engineering complexity.
    `
  },
  [AgentRole.RISK_ANALYST]: {
    model: "gemini-3-flash-preview", 
    thinkingBudget: 0,
    systemInstruction: `
      You are the **Senior Risk Analyst**.
      Your goal is to critique the plan.
      
      When adding tasks:
      - Add "Buffer" sessions or tasks.
      - Use 'PLACEHOLDER' type for "Security Audit" or "External Review" if scope is unknown.
      - Ensure '15m' breaks or checks are included using the correct unit.
    `
  },
  [AgentRole.STRATEGIST]: {
    model: "gemini-3-pro-preview",
    thinkingBudget: 16384, // High budget
    systemInstruction: `
      You are the **Chief Strategist**.
      Your goal is NOT just to list tasks, but to align the project with high-value outcomes.
      
      **Thinking Process:**
      - Before generating the plan, think deeply about the "Why".
      - Identify the 20% of tasks that drive 80% of the value (Pareto Principle).
      - Set 'strategicReward' and 'importance' high for these leverage points.
      - Deprioritize or flag low-value admin work.
      
      **Output:**
      - A refined project plan that emphasizes high-leverage activities.
      - In your reasoning, explicitly state the strategic "North Star" of this plan.
    `
  },
  [AgentRole.DB_ARCHITECT]: {
    model: "gemini-2.0-flash", // Fast, Precise, Code-Oriented
    thinkingBudget: 0, 
    systemInstruction: `
      You are the **Database Architect (DB Meister)**.
      Your goal is to bridge the gap between Project Goals and Database Reality.
      
      **Responsibilities:**
      1. **Schema Changes:** If the user implies data storage changes (e.g., "track sentiment"), you MUST generate the raw SQL in the 'migrationSQL' field.
      2. **Tasks:** Create tasks for the Flask-Farm developers to implement the API logic for these changes.
      
      **Migration SQL Rules:**
      - Write VALID PostgreSQL syntax.
      - Use 'IF NOT EXISTS' where possible.
      - Do not include transaction blocks (BEGIN/COMMIT).
      - Example: "ALTER TABLE framework.tasks ADD COLUMN sentiment_score FLOAT;"
      
      **Output:**
      - Populate 'migrationSQL' with the DDL commands.
      - Populate 'intent' as 'DB_MIGRATION_PROPOSAL'.
      - Create standard project tasks like "Update Flask Model", "Run Migration".
    `
  }
};

const responseSchema: Schema = {
  type: Type.OBJECT,
  properties: {
    intent: {
      type: Type.STRING,
      enum: ['INITIAL_SETUP', 'STRUCTURAL_OPTIMIZATION', 'RISK_MITIGATION', 'FEATURE_EXPANSION', 'UNDEFINED', 'DB_MIGRATION_PROPOSAL']
    },
    tagName: { type: Type.STRING },
    reasoning: { type: Type.STRING },
    migrationSQL: { type: Type.STRING, description: "Raw PostgreSQL DDL commands if schema changes are required." },
    sessions: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          id: { type: Type.STRING },
          name: { type: Type.STRING }
        },
        required: ["id", "name"]
      }
    },
    tasks: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          id: { type: Type.STRING },
          name: { type: Type.STRING },
          type: { type: Type.STRING, enum: ['STANDARD', 'MILESTONE', 'PLACEHOLDER'] },
          duration: { type: Type.NUMBER },
          unit: { type: Type.STRING, enum: ['15m', '1h', '1d', '1w'] },
          sessionId: { type: Type.STRING },
          dependencies: { 
            type: Type.ARRAY, 
            items: { type: Type.STRING }
          },
          status: { 
            type: Type.STRING, 
            enum: ['TODO', 'IN_PROGRESS', 'COMPLETED', 'BLOCKED'] 
          },
          // Tactical fields
          riskLevel: { type: Type.NUMBER },
          strategicReward: { type: Type.NUMBER },
          importance: { type: Type.NUMBER },
          vectorX: { type: Type.NUMBER },
          vectorY: { type: Type.NUMBER }
        },
        required: ["id", "name", "type", "duration", "unit", "dependencies", "status"]
      }
    }
  },
  required: ["intent", "tagName", "reasoning", "sessions", "tasks"]
};

// --- Client-Side Implementation ---
const queryClientSide = async (
  currentProject: ProjectState,
  userPrompt: string,
  role: AgentRole,
  settings: AppSettings
): Promise<AIResponse> => {
  
  // Resolve API Key: Check specific role key, then default process.env
  const specificKey = settings.agentApiKeys?.[role];
  const apiKey = specificKey || process.env.API_KEY;

  const ai = new GoogleGenAI({ apiKey });
  const config = AGENT_CONFIGS[role] || AGENT_CONFIGS[AgentRole.ARCHITECT];

  const fullSystemPrompt = `
    ${config.systemInstruction}

    Current Project Context:
    Name: ${currentProject.name}
    Description: ${currentProject.description}
    Existing Sessions: ${JSON.stringify(currentProject.sessions)}
    Existing Tasks JSON: ${JSON.stringify(currentProject.tasks)}
    
    User Request: "${userPrompt}"
    
    Rules:
    - Return the COMPLETE list of sessions and tasks.
    - Ensure valid JSON output matching the schema.
  `;

  const generateConfig: any = {
      systemInstruction: fullSystemPrompt,
      responseMimeType: "application/json",
      responseSchema: responseSchema,
    };
  
  // Apply thinking budget if defined
  if (config.thinkingBudget && config.thinkingBudget > 0) {
      generateConfig.thinkingConfig = { thinkingBudget: config.thinkingBudget };
  }

  const response = await ai.models.generateContent({
    model: config.model,
    contents: userPrompt,
    config: generateConfig
  });

  const text = response.text;
  if (!text) throw new Error("No response from AI");
  return JSON.parse(text) as AIResponse;
};

// --- Server-Side Implementation (Flask Proxy) ---
const queryRemoteBackend = async (
  currentProject: ProjectState,
  userPrompt: string,
  role: AgentRole,
  backendUrl: string,
  settings: AppSettings
): Promise<AIResponse> => {
  
  // Resolve API Key to pass to backend
  const specificKey = settings.agentApiKeys?.[role];

  const payload = {
    projectState: currentProject,
    prompt: userPrompt,
    agentRole: role,
    apiKey: specificKey // Optional: Backend will use its default if this is null/undefined
  };

  try {
    const response = await fetch(`${backendUrl}/api/consult`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error(`Backend Error: ${err}`);
    }

    return await response.json() as AIResponse;
  } catch (error) {
    throw new Error(`Failed to contact KVM4 Backend: ${error}`);
  }
};

export const queryMultiplexer = async (
  currentProject: ProjectState, 
  userPrompt: string,
  forcedRole: AgentRole,
  settings: AppSettings
): Promise<AIResponse> => {
  if (settings.useBackend && settings.backendUrl) {
    return queryRemoteBackend(currentProject, userPrompt, forcedRole, settings.backendUrl, settings);
  } else {
    return queryClientSide(currentProject, userPrompt, forcedRole, settings);
  }
};
